import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/supabase';

export type Account = Database['public']['Tables']['accounts']['Row'];
type AccountInsert = Database['public']['Tables']['accounts']['Insert'];
type AccountUpdate = Database['public']['Tables']['accounts']['Update'];

export class AccountService {
  static async create(data: Omit<AccountInsert, 'id' | 'created_at' | 'updated_at'>): Promise<Account> {
    // Criar nova conta e vincular usuário
    const { data: newAccount, error } = await (supabase as any)
      .rpc('create_new_account_and_link_user', {
        account_name: data.name
      });

    if (error) throw error;
    if (!newAccount) throw new Error('Failed to create account');

    // Buscar a conta recém-criada
    const { data: account, error: fetchError } = await supabase
      .from('accounts')
      .select('*')
      .eq('id', newAccount)
      .single();

    if (fetchError) throw fetchError;
    if (!account) throw new Error('Failed to fetch created account');

    // Atualizar metadata do usuário com a conta atual e refrescar token
    const { error: updateError } = await supabase.auth.updateUser({
      data: { current_account_id: (account as any).id }
    });
    if (updateError) throw updateError;
    await supabase.auth.refreshSession();

    // Disparar evento para notificar o AccountContext sobre a nova conta
    const event = new CustomEvent('accountCreated', { 
      detail: { 
        accountId: (account as any).id,
        account: account
      } 
    });
    window.dispatchEvent(event);

    return account;
  }

  static async update(id: string, data: AccountUpdate): Promise<Account> {
    const { data: updatedAccount, error } = await (supabase as any)
      .from('accounts')
      .update({
        ...data,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    if (!updatedAccount) throw new Error('Account not found');

    return updatedAccount;
  }

  static async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('accounts')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
}

// Re-export para manter compatibilidade com código existente
export const Account = AccountService;
